import { useEffect, useState } from "react";
import { AlertTriangle, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { SC_TIMEOUT_MS, fmt, fmtPeriod, fmtQuantity, scCode, scError, scMessage } from "./scUtils";

const D = OPS.sc.submitDialog;

/**
 * «إرسال الجرد». The caller saves pending edits BEFORE opening it, so
 * `missing` (items with no stored line) is the server's view. Items still
 * uncounted are submitted only with «اعتبرها صفراً» ticked (`zero_fill`): the
 * server then stores 0 for each and records which ones it filled.
 */
export function SubmitDialog({ open, onOpenChange, locationId, period, locationName, itemsN, missing, onChanged }) {
    const { t, lang } = useI18n();
    const [zeroFill, setZeroFill] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    // the server's count wins when it differs (an item added since the sheet loaded)
    const [serverMissing, setServerMissing] = useState(null);

    // a fresh confirmation every time the dialog opens
    useEffect(() => {
        if (open) { setZeroFill(false); setErr(""); setServerMissing(null); }
    }, [open]);

    const left = serverMissing ?? missing ?? 0;
    const counted = Math.max((itemsN || 0) - left, 0);
    const canConfirm = !busy && (left === 0 || zeroFill);

    const confirm = async () => {
        setBusy(true);
        setErr("");
        try {
            await api.post("/sc/sheet/submit",
                           { location_id: locationId, period, zero_fill: left > 0 && zeroFill },
                           { timeout: SC_TIMEOUT_MS });
            toast.success(t("stk_submitted"));
            onOpenChange(false);
            onChanged && (await onChanged());
        } catch (e) {
            if (scCode(e) === "SC_INCOMPLETE") {
                const n = Number(scError(e)?.missing);
                if (!Number.isNaN(n)) setServerMissing(n);
                setZeroFill(false);
            }
            const msg = scMessage(e, lang);
            setErr(msg || t("stk_offline_error"));
            // the server answered: the sheet behind the dialog may have moved on (window closed, submitted)
            if (msg && onChanged) onChanged();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
            <DialogContent data-testid={D.dialog} className="bg-[color:var(--bg-surface)] sm:max-w-md">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle>{t("stk_submit_title")}</DialogTitle>
                    <DialogDescription>{t("stk_submit_body")}</DialogDescription>
                </DialogHeader>

                <div className="rounded-[var(--radius-sm)] bg-[color:var(--bg-surface-2)] px-3 py-2 text-sm">
                    <p className="font-bold text-[color:var(--ink)]">{locationName}</p>
                    <p className="text-xs text-[color:var(--ink-muted)]">
                        {fmtPeriod(period, lang)} ·{" "}
                        <bdi dir="ltr">{fmtQuantity(counted, lang)}/{fmtQuantity(itemsN || 0, lang)}</bdi>{" "}
                        {t("stk_items_suffix")}
                    </p>
                </div>

                {left > 0 ? (
                    <div data-testid={D.missing} data-missing={left}
                         className="space-y-2 rounded-[var(--radius-sm)] bg-[color:var(--warning-soft)] px-3 py-2.5">
                        <p className="flex items-start gap-2 text-sm font-semibold text-[color:var(--warning-soft-fg)]">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            {fmt(t("stk_submit_missing"), { n: fmtQuantity(left, lang) })}
                        </p>
                        <label className="flex cursor-pointer items-start gap-2 text-sm text-[color:var(--ink)]">
                            <Checkbox data-testid={D.zeroFill} checked={zeroFill} disabled={busy}
                                      onCheckedChange={(v) => setZeroFill(v === true)} className="mt-0.5" />
                            <span>
                                <span className="font-bold">{t("stk_zero_fill")}</span>
                                <span className="block text-[11px] text-[color:var(--ink-muted)]">{t("stk_zero_fill_hint")}</span>
                            </span>
                        </label>
                    </div>
                ) : (
                    <p data-testid={D.missing} data-missing="0" className="text-sm font-semibold text-[color:var(--success-soft-fg)]">
                        {t("stk_submit_complete")}
                    </p>
                )}

                {err ? <p className="text-xs font-semibold text-[color:var(--danger)]">{err}</p> : null}

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" data-testid={D.cancel} disabled={busy}
                            onClick={() => onOpenChange(false)}>
                        {t("stk_cancel")}
                    </Button>
                    <Button type="button" data-testid={D.confirm} disabled={!canConfirm} onClick={confirm} className="gap-2">
                        <Send className="h-4 w-4 rtl:-scale-x-100" />
                        {busy ? t("stk_submitting") : t("stk_submit_confirm")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
